import React, { type ReactNode } from 'react'
import { StyledTableHeader } from './styles'
import { Box, Typography, useMediaQuery } from '@mui/material'
import CellBadge from './CellTypes/CellBadge'
import { useTheme } from '@mui/material/styles'
import GenericButton from '../GenericButton'
import { ButtonSize, ButtonType } from '../GenericButton/styles'
import Search from '../Search'
import { type DataObjectList } from '../../models/DataObject'
import { TableModalAction, type Row, type ScopeType } from './types'
import TableGuidelines from './TableGuidelines'
import { grey } from '../../colors'
import { capitalizeFirst } from '../../utils/utils'
import ToggleBadges from './ToggleBadges'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentRole } from '../../helpers/roleHelpers'
import { type UserRole } from '../../models/UserRoles'
import { SCOPE_TOGGLE_ALLOWED_ROLES } from '../../consts/roles'
import { ListFilter } from 'lucide-react'
import Dropdown, { type DropdownItemConfig } from '../Dropdown'
import { useUserProfile } from '../../hooks/useUserProfile'

interface TableTopBarActionsProps {
  list?: DataObjectList<Row> | null
  title: string | undefined
  label?: string | undefined
  exportCsv?: () => void
  exportCsvLoading?: boolean
  createActionTitle?: string
  customTopbarAction?: React.ReactNode
  handleCreateActionModal: () => void
  createButtonProps?: object | undefined
  onSearch?: (value: string) => void
  exportButtonProps?: object | undefined
  showScopeToggle?: boolean
  scopeToggleTitle?: string
  selectedScope?: ScopeType
  onScopeChange?: (scope: ScopeType) => void
  topbarActionsDropdownMenu?: DropdownItemConfig[] | ReactNode
}

const TableTopBarActions: React.FC<TableTopBarActionsProps> = ({ list, customTopbarAction, title, label, exportCsv, exportCsvLoading, createActionTitle, handleCreateActionModal, createButtonProps, onSearch, exportButtonProps, showScopeToggle, scopeToggleTitle, selectedScope, onScopeChange, topbarActionsDropdownMenu }): JSX.Element => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const filterAndSearchDirection = isMobile ? 'column' : 'row'

  const showCreateAction = list?.actions?.includes(TableModalAction.CREATE)
  const showExportAction = list?.actions?.includes(TableModalAction.READ)
  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []
  const currentRole = getCurrentRole(currentUserRoles)
  const allowedRoleNames = SCOPE_TOGGLE_ALLOWED_ROLES.map(role => role.toString())
  const shouldShowToggleBadges = showScopeToggle && scopeToggleTitle && selectedScope && onScopeChange && currentRole && allowedRoleNames.includes(currentRole.roleName)

  const cdgTopbarDefatultActionsDropdownMenu = [
    {
      text: 'Export (.csv)',
      action: () => { exportCsv?.() },
      loading: exportCsvLoading,
      disabled: exportCsvLoading || !showExportAction,
      visible: exportCsv !== undefined,
      ...exportButtonProps
    }
  ]

  // Determine which menuItems to use
  const menuItems = topbarActionsDropdownMenu ?? cdgTopbarDefatultActionsDropdownMenu

  // Check if Dropdown should be shown (at least one item visible)
  const shouldShowDropdown = Array.isArray(menuItems)
    ? menuItems.some(item => item.visible !== false)
    : true

  return (
    <StyledTableHeader isMobile={isMobile}>
      <Box gap={theme.spacing(1)} display={'flex'} alignItems={'center'}>
        <Typography fontWeight={600} fontSize='18px'>{capitalizeFirst(title ?? '')}</Typography>
        {label && <CellBadge color='primary' label={label} />}
        <TableGuidelines />
      </Box>
      <Box display='flex' gap={3} flexDirection={filterAndSearchDirection} alignItems={'center'}>
        {shouldShowToggleBadges && (
          <ToggleBadges
            title={scopeToggleTitle}
            selectedScope={selectedScope}
            onScopeChange={onScopeChange}
          />
        )}

        <GenericButton
          icon={<ListFilter color={grey[250]} opacity={0.4} size={20} />}
          disabled
          buttonType={ButtonType.Secondary}
          buttonSize={ButtonSize.Medium}
        />
        {onSearch && <Search onSearch={onSearch} />}

        {
          createActionTitle?.trim() && showCreateAction &&
          <GenericButton
            buttonType={ButtonType.Primary}
            label={createActionTitle}
            buttonSize={ButtonSize.Medium}
            fontWeight={'bold'}
            {...createButtonProps}
            onClick={() => {
              handleCreateActionModal()
            }}
          />
        }

        {customTopbarAction}
        {
          shouldShowDropdown && (
            <Dropdown
              menuItems={menuItems}
              dropdownType='action'
              extraButtonProps={{
                style: { p: 0 },
                circularProgressStyles: { color: grey[300] },
                buttonType: ButtonType.Ghost
              }}
            />
          )
        }
      </Box>
    </StyledTableHeader >
  )
}

export default React.memo(TableTopBarActions)
